import { hasOrphanedToken, hasStaleBoundToken, shouldExchangeSessionTokenCookies } from '../auth/auth-token';
import { TOKEN_COOKIE_NAME, WAND_TOKEN_COOKIE_NAME } from '../auth/constants';
import { getHostContextFromHeaders } from '../host/host-context';
import type { CookieReconcileContext, CookieReconcileResult } from '../utils/cookie-reconciliation';
import {
    buildCookieHeader,
    extractSetCookieHeaders,
    parseCookieHeader,
    parseSetCookieString,
    setCookieInHeader,
} from '../utils/cookie-utils';

export const WAND_ANALYTICS_COOKIE_NAME = 'wand_analytics';
export const ANALYTICS_PARAMS_PATH = '/api/analytics_params';
const ANALYTICS_FETCH_TIMEOUT_MS = 15_000;

/**
 * Ad-click + first-touch UTM query params we forward to Laravel on the analytics_params call so its
 * CaptureAcquisitionParams middleware can mint the first-party cookies (`_gcl_aw`, `_fbc`, `rdt_cid`,
 * `ttclid`, `msclkid`, `wand_utm_*`) — the wand.com side of WAND-6792. wand.com pages are served by
 * Next.js, so this reconcile round-trip is the earliest Laravel touchpoint that sees the landing URL.
 * Maps each param to the cookie it lands in (for Reddit/TikTok/Microsoft the two are identical), so we
 * can tell when one still needs capturing.
 */
const ACQUISITION_PARAM_COOKIES: Record<string, string> = {
    gclid: '_gcl_aw',
    fbclid: '_fbc',
    rdt_cid: 'rdt_cid',
    ttclid: 'ttclid',
    msclkid: 'msclkid',
    utm_source: 'wand_utm_source',
    utm_medium: 'wand_utm_medium',
    utm_campaign: 'wand_utm_campaign',
    utm_content: 'wand_utm_content',
    utm_term: 'wand_utm_term',
};

/** The whitelisted acquisition params present on a request URL, if any. */
function acquisitionParamsFromUrl(url: string): URLSearchParams {
    const present = new URLSearchParams();

    let search: URLSearchParams;
    try {
        search = new URL(url).searchParams;
    } catch {
        return present;
    }

    for (const param of Object.keys(ACQUISITION_PARAM_COOKIES)) {
        const value = search.get(param);
        if (value) {
            present.set(param, value);
        }
    }

    return present;
}

/** True when the URL carries an acquisition param whose target cookie isn't already set. */
function hasUncapturedAcquisitionParam(acqParams: URLSearchParams, cookieHeader: string): boolean {
    const cookies = parseCookieHeader(cookieHeader);
    for (const param of acqParams.keys()) {
        const cookieName = ACQUISITION_PARAM_COOKIES[param];
        if (cookieName && !cookies.has(cookieName)) {
            return true;
        }
    }

    return false;
}

/**
 * The visitor's country from Cloudflare's `CF-IPCountry` header, forwarded so Laravel's consent gate
 * can apply its jurisdiction fallback (the edge->origin subrequest wouldn't otherwise carry it).
 */
function ipCountryFromRequest(request: Pick<Request, 'headers'>): string | undefined {
    const country = request.headers?.get?.('cf-ipcountry') ?? undefined;
    return country && country !== 'XX' ? country : undefined;
}

export type WandAnalyticsParams = {
    adElig: 0 | 1;
    userId?: string;
    otUserToken?: string;
    email?: { sha256: string } | null;
};

/** What one analytics_params call returns to every caller that shares it. */
type AnalyticsMintResponse = {
    setCookieHeaders: string[];
    /** Laravel rejected the bearer: the token is expired, revoked or for the wrong client. */
    unauthorized: boolean;
};

/** The credential presented as the bearer, and the cookie it came from. */
type MintBearer = {
    token: string;
    cookieName: typeof TOKEN_COOKIE_NAME | typeof WAND_TOKEN_COOKIE_NAME;
};

/** Expires the shared auth client's cookie, which it writes host-only (no Domain). */
const WAND_TOKEN_CLEAR_COOKIE = `${WAND_TOKEN_COOKIE_NAME}=; Path=/; Secure; SameSite=Lax; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0`;

const reconcilePromises = new Map<string, Promise<AnalyticsMintResponse>>();

function extractAnalyticsCookieValue(setCookieHeaders: readonly string[]): string | undefined {
    for (const header of setCookieHeaders) {
        const parsed = parseSetCookieString(header);
        if (parsed?.name === WAND_ANALYTICS_COOKIE_NAME && parsed.value) {
            return parsed.value;
        }
    }

    return undefined;
}

/**
 * POSTs the analytics-params endpoint and returns only the response's Set-Cookie headers and whether
 * it rejected the bearer. Keeping the caller's own cookie header out of the return lets callers with
 * identical lookup inputs share the network response without sharing the downstream cookie result.
 * Fails open with no headers.
 */
async function fetchAnalyticsSetCookieHeaders(
    cookieHeader: string,
    wandWebsiteUrl: string,
    acqParams: URLSearchParams,
    ipCountry?: string,
    bearerToken?: string,
): Promise<AnalyticsMintResponse> {
    try {
        const query = acqParams.toString();
        const url = query
            ? `${wandWebsiteUrl}${ANALYTICS_PARAMS_PATH}?${query}`
            : `${wandWebsiteUrl}${ANALYTICS_PARAMS_PATH}`;

        const headers: Record<string, string> = {
            Accept: 'application/json',
            Cookie: cookieHeader,
        };
        if (ipCountry) {
            headers['CF-IPCountry'] = ipCountry;
        }
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            signal: AbortSignal.timeout(ANALYTICS_FETCH_TIMEOUT_MS),
        });

        if (!response.ok) {
            return { setCookieHeaders: [], unauthorized: response.status === 401 };
        }

        return { setCookieHeaders: extractSetCookieHeaders(response), unauthorized: false };
    } catch {
        return { setCookieHeaders: [], unauthorized: false };
    }
}

/**
 * Applies a (possibly shared) analytics_params response to this caller's own cookie header.
 *
 * A 401 means the bearer is bad. When that bearer was `wand_token`, the response expires it and the
 * downstream request drops it, so the next request mints from the site session or anonymously. A 401 on
 * the legacy `token` changes nothing, as before: the auth reconciler owns that cookie.
 */
function applyAnalyticsSetCookies(
    cookieHeader: string,
    response: AnalyticsMintResponse,
    bearer: MintBearer | undefined,
): CookieReconcileResult {
    if (response.unauthorized && bearer?.cookieName === WAND_TOKEN_COOKIE_NAME) {
        const cookies = parseCookieHeader(cookieHeader);
        cookies.delete(WAND_TOKEN_COOKIE_NAME);
        return {
            downstreamCookieHeader: buildCookieHeader(cookies),
            setCookieHeaders: [WAND_TOKEN_CLEAR_COOKIE],
        };
    }

    const { setCookieHeaders } = response;
    const cookieValue = extractAnalyticsCookieValue(setCookieHeaders);

    return {
        downstreamCookieHeader:
            cookieValue !== undefined
                ? setCookieInHeader(cookieHeader, WAND_ANALYTICS_COOKIE_NAME, cookieValue)
                : cookieHeader,
        setCookieHeaders,
    };
}

/**
 * Whether the auth reconciler — running in the same parallel pass, over this same cookie header —
 * will clear this token. On web hosts it treats a token with no site session as orphaned
 * (`hasOrphanedToken`) and, when the app is on the shared auth client, a token bound to an earlier
 * session as stale (`hasStaleBoundToken`), and emits clear-cookie headers for both. Minting analytics
 * from a token that is being cleared in the same response would fire a bearer-authenticated
 * `/api/analytics_params` call for today's real orphaned-cookie traffic, and for a stale token Laravel,
 * finding no user on the site session, would mint the earlier session's identity. So the bearer stays
 * withheld whenever the auth reconciler clears the token.
 *
 * Embedded (non-web) hosts are outside the auth reconciler's gate, so a sessionless token there is
 * left alone and the mint does apply.
 */
function tokenIsClearedByAuthReconciler(
    request: Pick<Request, 'url' | 'headers'>,
    cookies: Map<string, string>,
    context?: CookieReconcileContext,
): boolean {
    const hostContext = getHostContextFromHeaders(request.headers ?? new Headers(), request.url);
    return (
        shouldExchangeSessionTokenCookies(hostContext) &&
        (hasOrphanedToken(cookies) || hasStaleBoundToken(cookies, context))
    );
}

/**
 * Ensures `wand_analytics` exists, minting it from the account behind a token-only (OAuth) login or,
 * otherwise, via Laravel's session-based `/api/analytics_params`.
 * Fails open on error — returns the original cookie header with no Set-Cookie headers.
 */
export async function reconcileAnalyticsCookiesForRequest(
    request: Pick<Request, 'url' | 'headers'>,
    cookieHeader: string,
    wandWebsiteUrl: string,
    context?: CookieReconcileContext,
): Promise<CookieReconcileResult> {
    const acqParams = acquisitionParamsFromUrl(request.url);
    const cookies = parseCookieHeader(cookieHeader);
    const hasAnalyticsCookie = cookies.has(WAND_ANALYTICS_COOKIE_NAME);

    // Skip the round-trip only when there's nothing to do: the cookie already exists and there's no
    // new ad-click / UTM param that still needs minting server-side.
    if (hasAnalyticsCookie && !hasUncapturedAcquisitionParam(acqParams, cookieHeader)) {
        return {
            downstreamCookieHeader: cookieHeader,
            setCookieHeaders: [],
        };
    }

    return reconcileAnalyticsCookiesDeduped(
        cookieHeader,
        wandWebsiteUrl,
        acqParams,
        ipCountryFromRequest(request),
        bearerTokenForMint(request, cookies, context),
    );
}

/**
 * The access token to present to Laravel alongside the cookie header, if any.
 *
 * `wand_token` (written by the shared auth client) wins whenever it is present, whatever the app's auth
 * client setting says: a visitor who holds one keeps their identity, and their ads eligibility, when the
 * setting is switched either way. It is not bound to the site session, so the orphan and stale rules
 * below do not apply to it.
 *
 * Sent whenever the request carries one, not only when a site session is absent: the worker cannot tell
 * an authenticated session cookie from the anonymous one this very endpoint hands every visitor, and
 * Laravel can. It prefers its own session user and falls back to the token, so passing both and letting
 * it decide is what keeps a signed-in user off the anonymous mint.
 *
 * A legacy `token` is withheld only when the auth reconciler is clearing it in the same parallel pass:
 * as orphaned, because that user is being logged out and belongs on the anonymous mint exactly as today,
 * or as stale, because it belongs to an earlier session than the one on this request.
 */
function bearerTokenForMint(
    request: Pick<Request, 'url' | 'headers'>,
    cookies: Map<string, string>,
    context?: CookieReconcileContext,
): MintBearer | undefined {
    const wandToken = cookies.get(WAND_TOKEN_COOKIE_NAME);
    if (wandToken) {
        return { token: wandToken, cookieName: WAND_TOKEN_COOKIE_NAME };
    }

    const token = cookies.get(TOKEN_COOKIE_NAME);
    if (!token || tokenIsClearedByAuthReconciler(request, cookies, context)) {
        return undefined;
    }

    return { token, cookieName: TOKEN_COOKIE_NAME };
}

/**
 * Coalesces parallel POSTs from one caller within a worker/middleware isolate.
 *
 * Every input Laravel uses is part of the key: the complete cookie header, bearer, acquisition params,
 * and country. During a login race, a request with only the anonymous session cookie and one that also
 * carries the freshly-set token must not share a response even though their session value matches.
 *
 * Only the response's Set-Cookie headers are shared; each caller then applies them to its own cookie
 * header, so a coalesced caller never inherits another request's cookies downstream.
 */
async function reconcileAnalyticsCookiesDeduped(
    cookieHeader: string,
    wandWebsiteUrl: string,
    acqParams: URLSearchParams,
    ipCountry?: string,
    bearer?: MintBearer,
): Promise<CookieReconcileResult> {
    const hasSession = parseCookieHeader(cookieHeader).has('session');
    if (!hasSession && !bearer) {
        return applyAnalyticsSetCookies(
            cookieHeader,
            await fetchAnalyticsSetCookieHeaders(cookieHeader, wandWebsiteUrl, acqParams, ipCountry),
            bearer,
        );
    }

    const key = JSON.stringify([
        wandWebsiteUrl,
        cookieHeader,
        bearer?.token ?? null,
        acqParams.toString(),
        ipCountry ?? null,
    ]);
    const existing = reconcilePromises.get(key);
    if (existing) {
        return applyAnalyticsSetCookies(cookieHeader, await existing, bearer);
    }

    const promise = fetchAnalyticsSetCookieHeaders(
        cookieHeader,
        wandWebsiteUrl,
        acqParams,
        ipCountry,
        bearer?.token,
    ).finally(() => {
        if (reconcilePromises.get(key) === promise) {
            reconcilePromises.delete(key);
        }
    });
    reconcilePromises.set(key, promise);

    return applyAnalyticsSetCookies(cookieHeader, await promise, bearer);
}

export function parseWandAnalyticsCookie(cookieHeader: string): WandAnalyticsParams | null {
    const raw = parseCookieHeader(cookieHeader).get(WAND_ANALYTICS_COOKIE_NAME);
    if (!raw) {
        return null;
    }

    try {
        const decoded = Buffer.from(raw, 'base64').toString('utf8');
        const parsed = JSON.parse(decoded) as unknown;
        if (!parsed || typeof parsed !== 'object' || !('adElig' in parsed)) {
            return null;
        }

        const adElig = (parsed as WandAnalyticsParams).adElig;
        if (adElig !== 0 && adElig !== 1) {
            return null;
        }

        return parsed as WandAnalyticsParams;
    } catch {
        return null;
    }
}

export function parseWandAnalyticsUserId(cookieHeader: string): string | null {
    const userId = parseWandAnalyticsCookie(cookieHeader)?.userId;

    return typeof userId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId)
        ? userId
        : null;
}
